import React from "react";
import { Edges } from "@react-three/drei";
import { DoubleSide, Texture } from "three";
import { InstallationType } from "../../../../../../core/common_types";
import {
  MM,
  SPESSORE_MURO,
  PORTINA_LAT_LARGHEZZA,
  PORTINA_LAT_ALTEZZA,
  PORTINA_LAT_CORNICE_W,
  PORTINA_LAT_GAP,
  C_PANNELLO,
  C_BORDO_CHIARO,
} from "./constants";

export const PORTINA_LAT_RAIL_H = MM(10);

export type PortinaLateraleProps = {
  installazione: InstallationType;
  larghezza: number;
  corniceColorCode: Texture | undefined;
  pannelloColorCode: Texture | undefined;
  panelCount: number;
  apertura: "sx" | "dx";
  handleColor?: string;
};

export const PortinaLaterale: React.FC<PortinaLateraleProps> = ({
  installazione,
  larghezza,
  corniceColorCode,
  pannelloColorCode,
  panelCount,
  apertura,
  handleColor = "#111111",
}) => {
  const texturaCornice  = corniceColorCode;
  const texturaPannello = pannelloColorCode;

  const SM  = SPESSORE_MURO;
  const cw  = PORTINA_LAT_CORNICE_W;
  const fd  = MM(8);

  const xLeft   = larghezza + PORTINA_LAT_GAP;
  const xRight  = xLeft + PORTINA_LAT_LARGHEZZA;
  const xCenter = (xLeft + xRight) / 2;
  const hh      = PORTINA_LAT_ALTEZZA / 2;

  const zFace   = installazione === InstallationType.in_luce ? SM / 2 + MM(2) : -SM / 2 - MM(2);
  const zHandle = installazione === InstallationType.in_luce ? SM / 2 + MM(2) : -SM / 2 - MM(25);

  const pannelloW = PORTINA_LAT_LARGHEZZA - 2 * cw;
  const pannelloH = PORTINA_LAT_ALTEZZA   - 2 * cw;

  const numRails   = panelCount - 1;
  const singlePanH = (pannelloH - numRails * PORTINA_LAT_RAIL_H) / panelCount;

  const corniceMatProps = {
    map: texturaCornice,
    color: texturaCornice ? "#ffffff" : "#888888",
    side: DoubleSide,
  };
  const pannelloMatProps = {
    map: texturaPannello,
    color: texturaPannello ? "#ffffff" : C_PANNELLO,
    side: DoubleSide,
  };

  const pannelloKey = texturaPannello?.uuid ?? 'pannello-default';
  const corniceKey  = texturaCornice?.uuid  ?? 'cornice-default';

  const panels: JSX.Element[] = [];
  for (let i = 0; i < panelCount; i++) {
    const yBottom      = cw + i * (singlePanH + PORTINA_LAT_RAIL_H);
    const yCenterPanel = yBottom + singlePanH / 2;
    panels.push(
      <mesh key={`pan_${i}`} position={[xCenter, yCenterPanel, zFace]}>
        <boxGeometry args={[pannelloW, singlePanH, fd / 2]} />
        <meshStandardMaterial key={pannelloKey} {...pannelloMatProps} />
      </mesh>
    );
    if (i < panelCount - 1) {
      const yRail = yBottom + singlePanH + PORTINA_LAT_RAIL_H / 2;
      panels.push(
        <mesh key={`rail_${i}`} position={[xCenter, yRail, zFace]}>
          <boxGeometry args={[pannelloW, PORTINA_LAT_RAIL_H, fd]} />
          <meshStandardMaterial key={corniceKey} {...corniceMatProps} />
          <Edges color={C_BORDO_CHIARO} />
        </mesh>
      );
    }
  }

  return (
    <group>
      {panels}
      {/* Bordo superiore */}
      <mesh position={[xCenter, PORTINA_LAT_ALTEZZA - cw / 2, zFace]}>
        <boxGeometry args={[PORTINA_LAT_LARGHEZZA, cw, fd]} />
        <meshStandardMaterial key={corniceKey} {...corniceMatProps} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Bordo inferiore */}
      <mesh position={[xCenter, cw / 2, zFace]}>
        <boxGeometry args={[PORTINA_LAT_LARGHEZZA, cw, fd]} />
        <meshStandardMaterial key={corniceKey} {...corniceMatProps} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Bordo sinistro */}
      <mesh position={[xLeft + cw / 2, hh, zFace]}>
        <boxGeometry args={[cw, PORTINA_LAT_ALTEZZA, fd]} />
        <meshStandardMaterial key={corniceKey} {...corniceMatProps} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Bordo destro */}
      <mesh position={[xRight - cw / 2, hh, zFace]}>
        <boxGeometry args={[cw, PORTINA_LAT_ALTEZZA, fd]} />
        <meshStandardMaterial key={corniceKey} {...corniceMatProps} />
        <Edges color={C_BORDO_CHIARO} />
      </mesh>
      {/* Maniglia a leva — lato dipende da apertura, profondità da installazione */}
      {(() => {
        const zLeva   = installazione === InstallationType.in_luce ? zFace + MM(6) : zFace - MM(6);
        const zDir    = installazione === InstallationType.in_luce ? 1 : -1;
        const sx      = apertura === "sx" ? 1 : -1;
        const handleX = apertura === "sx" ? xLeft + cw + MM(50) : xRight - cw - MM(50);
        return (
          <group position={[handleX, hh, zLeva]} scale={[sx, 1, zDir]}>
            {/* Rosetta principale */}
            <mesh position={[0, 0, MM(4)]} rotation={[-Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[MM(30), MM(30), MM(8), 32]} />
              <meshStandardMaterial color={handleColor} metalness={0.9} roughness={0.15} side={DoubleSide} />
            </mesh>
            {/* Nucleo cilindrico */}
            <mesh position={[0, 0, MM(17)]} rotation={[-Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[MM(10), MM(13), MM(18), 16]} />
              <meshStandardMaterial color={handleColor} metalness={0.9} roughness={0.1} side={DoubleSide} />
            </mesh>
            {/* Leva verso +x inclinata verso il basso */}
            <group rotation={[0, 0, -0.18]}>
              <mesh position={[MM(45), 0, MM(24)]} rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[MM(6), MM(10), MM(90), 16]} />
                <meshStandardMaterial color={handleColor} metalness={0.92} roughness={0.07} side={DoubleSide} />
              </mesh>
              <mesh position={[MM(90), 0, MM(24)]}>
                <sphereGeometry args={[MM(6), 16, 12]} />
                <meshStandardMaterial color={handleColor} metalness={0.92} roughness={0.07} side={DoubleSide} />
              </mesh>
            </group>
            {/* Rosetta serratura */}
            <mesh position={[0, -MM(100), MM(3)]} rotation={[-Math.PI / 2, 0, 0]}>
              <cylinderGeometry args={[MM(20), MM(20), MM(6), 32]} />
              <meshStandardMaterial color={handleColor} metalness={0.9} roughness={0.15} side={DoubleSide} />
            </mesh>
          </group>
        );
      })()}
    </group>
  );
};
